// Root route — shell + auth gate.
//
// Renders the AppShell for authenticated users on all routes other than
// /login. The AuthGate provider is mounted in main.tsx, *above* the
// router, so this component just reads from useAuth and decides what to
// show.

import {
  createRootRouteWithContext,
  Outlet,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { AppShell } from '@/components/layout/app-shell';
import { useAuth } from '@/hooks/useAuth';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorBoundary } from '@/components/ui/error-boundary';

export interface RouterContext {
  queryClient: QueryClient;
}

function RootShell(): JSX.Element {
  const { loading, isAuthorized } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isLogin = location.pathname.startsWith('/login');

  useEffect(() => {
    if (!loading && !isAuthorized && !isLogin) {
      void navigate({ to: '/login', replace: true });
    }
  }, [loading, isAuthorized, isLogin, navigate]);

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center p-8">
        <Card className="w-full max-w-[420px]">
          <Skeleton className="h-6 w-32 mb-4" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-3/4" />
        </Card>
      </div>
    );
  }

  if (isLogin || !isAuthorized) {
    // Login page is full-bleed (no shell).
    return (
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    );
  }

  return (
    <AppShell>
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </AppShell>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootShell,
});
